<script setup lang="ts">
import { ref } from 'vue'
import * as ExcelJS from 'exceljs'
import type { Tender } from '@/types/tender'
import LoaderDefault from './common/LoaderDefault.vue'

import { useAuthStore } from '@/stores/auth' // Import your auth store

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

const authStore = useAuthStore() // Initialize auth store

const fileInput = ref<HTMLInputElement | null>(null)
const fileName = ref('')
const fileSize = ref('')
const uploading = ref(false)
const errorMessage = ref('')
const savingToDatabase = ref(false)
const extractedLinksCount = ref(0)
const skippedRecords = ref({
  blankRefNo: 0,
  expiredDeadline: 0,
  watchlistSkipped: 0, // Add watchlist skipped count
  // zeroCost: 0
})

const emit = defineEmits<{
  tenderUploaded: []
  close: []
}>()

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileUpload = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files?.length) return

  const file = input.files[0]
  fileName.value = file.name
  fileSize.value = formatFileSize(file.size)
  uploading.value = true
  errorMessage.value = ''
  savingToDatabase.value = false
  extractedLinksCount.value = 0
  skippedRecords.value = { blankRefNo: 0, expiredDeadline: 0, watchlistSkipped: 0 }
  // skippedRecords.value = { blankRefNo: 0, expiredDeadline: 0, zeroCost: 0 }

  // Check if user is logged in and is admin
  if (!authStore.isAuthenticated) {
    errorMessage.value = 'You must be logged in to upload tenders'
    return
  }

  if (authStore.user?.userType !== 'admin') {
    errorMessage.value = 'Only administrators can upload tenders'
    return
  }

  try {
    const tenders = await processExcelFileWithExcelJS(file)

    if (tenders.length === 0) {
      throw new Error('No valid tenders found in the file')
    }

    console.log(`📥 Processed ${tenders.length} tenders`)
    console.log(`GeM Tenders: ${tenders.filter(t => t.sheetType === 'GeM').length}`)
    console.log(`Non-GeM Tenders: ${tenders.filter(t => t.sheetType === 'Non-GeM').length}`)
    console.log(`Skipped ${skippedRecords.value.blankRefNo} blank Reference No`)
    console.log(`Skipped ${skippedRecords.value.expiredDeadline} expired deadlines`)
    console.log(`Skipped ${skippedRecords.value.watchlistSkipped} watchlist tenders`)
    // console.log(`Skipped ${skippedRecords.value.zeroCost} zero cost tenders`)

    // Save to database
    await saveToDatabase(tenders)

    // Clear file input
    if (fileInput.value) {
      fileInput.value.value = ''
    }

    // Notify parent
    emit('tenderUploaded')

    // Close after delay
    setTimeout(() => {
      emit('close')
    }, 1000)

  } catch (error) {
    console.error('Upload error:', error)
    errorMessage.value = error instanceof Error ? error.message : 'Failed to process Excel file'
  } finally {
    uploading.value = false
    savingToDatabase.value = false
  }
}

// Define a partial tender type for the processing stage
type PartialTender = Omit<Tender, 'id' | 'createdAt' | 'updatedAt' | 'uploadedBy' | 'assignedAgent' | 'assignedAgentId'>

const processExcelFileWithExcelJS = async (file: File): Promise<Tender[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = async (e) => {
      try {
        const data = e.target?.result
        if (!data) {
          reject(new Error('Failed to read file'))
          return
        }

        const workbook = new ExcelJS.Workbook()
        await workbook.xlsx.load(data as ArrayBuffer)

        const tenders: PartialTender[] = []
        // Track T247 IDs seen in this upload to handle duplicates within the same file
        const seenT247Ids = new Set<string>()

        workbook.eachSheet((worksheet) => {
          const sheetName = worksheet.name

          // Determine default sheet type based on sheet name
          let defaultSheetType: 'Non-GeM' | 'GeM' = 'Non-GeM'

          const sheetNameLower = sheetName.toLowerCase()
          if (sheetNameLower.includes('gem') && !sheetNameLower.includes('non-gem') && !sheetNameLower.includes('nongem')) {
            defaultSheetType = 'GeM'
          } else if (sheetNameLower.includes('non-gem') || sheetNameLower.includes('nongem')) {
            defaultSheetType = 'Non-GeM'
          }

          console.log(`📋 Processing sheet: ${sheetName} (Default Type: ${defaultSheetType})`)

          // Get headers
          const headerRow = worksheet.getRow(1)
          const headers: Record<string, number> = {}

          headerRow.eachCell((cell, colNumber) => {
            if (cell.value) {
              headers[String(cell.value).trim()] = colNumber
            }
          })

          // Helper to find column
          const getCol = (names: string[]): number | null => {
            for (const name of names) {
              if (headers[name]) return headers[name]
            }
            return null
          }

          const t247IdCol = getCol(['T247 ID', 'T247ID', 'TENDER ID', 'Tender ID'])
          const referenceNoCol = getCol(['REFERENCE NO', 'REF NO', 'Reference No', 'Reference Number'])
          const tenderBriefCol = getCol(['TENDER BRIEF', 'BRIEF', 'DESCRIPTION', 'Tender Brief'])
          const deadlineCol = getCol(['Deadline', 'DEADLINE', 'CLOSING DATE', 'Closing Date'])
          const locationCol = getCol(['LOCATION', 'CITY', 'ADDRESS', 'Location'])
          const organizationCol = getCol(['Organization', 'ORG', 'DEPARTMENT', 'Organization Name'])

          // Additional columns to fetch
          const msmeExemptionCol = getCol(['MSME Exemption', 'MSME', 'MSME Exemptions', 'MSME Status'])
          const startupExemptionCol = getCol(['Startup Exemption', 'STARTUP', 'Startup Exemptions', 'Startup Status'])
          const quantityCol = getCol(['Quantity', 'QTY', 'Quantity Required', 'QTY.', 'Nos', 'Units'])
          const eligibilityCriteriaCol = getCol(['Eligibility Criteria', 'ELIGIBILITY', 'Eligibility', 'Criteria', 'Qualification Criteria'])
          const checklistCol = getCol(['Checklist', 'DOCUMENTS', 'Required Documents', 'Document Checklist', 'Documents Required'])

          // NEW: Document required from seller column (GeM specific)
          const documentRequiredFromSellerCol = getCol([
            'Document required from seller',
            'Document required from seller',
            'Seller Documents Required',
            'Required Documents from Seller',
            'Seller Docs'
          ])

          // Check for Sheet Type column in the Excel file itself
          const sheetTypeCol = getCol(['Sheet Type', 'SHEET TYPE', 'TYPE', 'Category', 'sheettype'])

          // Helper function to parse cost value properly
          const parseCostValue = (value: unknown): number => {
            if (value === null || value === undefined || value === '') {
              return 0
            }

            if (typeof value === 'number') {
              return value
            }

            const stringValue = String(value).trim()

            // Remove currency symbols, commas, and other non-numeric characters
            const numericString = stringValue
              .replace(/[^\d.,]/g, '')
              .replace(/,/g, '')

            const parsedValue = parseFloat(numericString)

            if (isNaN(parsedValue)) {
              const numberMatch = stringValue.match(/\d+(\.\d+)?/)
              if (numberMatch) {
                return parseFloat(numberMatch[0])
              }
              return 0
            }

            return parsedValue
          }

          // Process rows
          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return // Skip header

            try {
              // Helper function to get field value with NA fallback
              const getFieldValue = (col: number | null, defaultValue: string = 'NA'): string => {
                if (!col) return defaultValue

                const cell = row.getCell(col)
                const value = cell.value
                if (value === null || value === undefined || value === '') {
                  return defaultValue
                }

                const stringValue = String(value).trim()
                return stringValue || defaultValue
              }

              // Helper function to get quantity value (special case - default to "0")
              const getQuantityValue = (col: number | null): string => {
                if (!col) return '0'

                const cell = row.getCell(col)
                const value = cell.value
                if (value === null || value === undefined || value === '') {
                  return '0'
                }

                const stringValue = String(value).trim()
                return stringValue || '0'
              }

              const referenceNo = referenceNoCol ? String(row.getCell(referenceNoCol).value || '').trim() : ''

              // Skip if no reference number
              if (!referenceNo) {
                skippedRecords.value.blankRefNo++
                return
              }

              let t247Id = t247IdCol ? String(row.getCell(t247IdCol).value || '').trim() : ''

              // Skip if no T247 ID
              if (!t247Id) {
                return
              }

              // If this T247 ID already exists in this batch (same ID, different tender),
              // append the referenceNo suffix to make it unique in the DB
              if (seenT247Ids.has(t247Id)) {
                t247Id = `${t247Id}_${referenceNo.replace(/[^a-zA-Z0-9]/g, '_')}`
              }
              seenT247Ids.add(t247Id)

              const tenderBriefCell = tenderBriefCol ? row.getCell(tenderBriefCol) : null
              let tenderBriefText = ''
              let tenderBriefLink = ''

              if (tenderBriefCell) {
                tenderBriefText = String(tenderBriefCell.text || tenderBriefCell.toString() || '').trim()

                // Extract hyperlink
                if (tenderBriefCell.hyperlink) {
                  tenderBriefLink = tenderBriefCell.hyperlink
                  extractedLinksCount.value++
                }
              }

              const deadline = deadlineCol ? row.getCell(deadlineCol).value : ''
              const formattedDeadline = formatDate(String(deadline || ''))

              // Skip if deadline is expired
              if (formattedDeadline) {
                console.log('Raw formattedDeadline:', formattedDeadline)

                const deadlineDate = new Date(formattedDeadline)
                console.log('Parsed deadlineDate:', deadlineDate)
                console.log('Is deadlineDate valid?', !isNaN(deadlineDate.getTime()))

                if (isNaN(deadlineDate.getTime())) {
                  console.warn('Invalid date format:', formattedDeadline)
                  // Optionally, you might want to still include it or handle differently
                  return
                }

                deadlineDate.setHours(0, 0, 0, 0)

                const today = new Date()
                today.setHours(0, 0, 0, 0)

                console.log('Normalized deadline:', deadlineDate.toISOString())
                console.log('Normalized today:', today.toISOString())
                console.log('Deadline < today?', deadlineDate < today)

                if (deadlineDate < today) {
                  console.log('Skipping expired tender with deadline:', formattedDeadline)
                  skippedRecords.value.expiredDeadline++
                  return
                }

                console.log('Keeping tender with deadline:', formattedDeadline)
              }

              const location = locationCol ? String(row.getCell(locationCol).value || '').trim() : ''
              const organization = organizationCol ? String(row.getCell(organizationCol).value || '').trim() : ''

              // Determine final sheet type
              let finalSheetType: 'Non-GeM' | 'GeM' = defaultSheetType

              // Check if sheet type is specified in the Excel column
              if (sheetTypeCol) {
                const excelSheetType = String(row.getCell(sheetTypeCol).value || '').trim()
                if (excelSheetType) {
                  const normalizedType = excelSheetType.toLowerCase()
                  if (normalizedType === 'gem' || normalizedType === 'g.e.m.' || normalizedType === 'g e m') {
                    finalSheetType = 'GeM'
                  } else if (normalizedType === 'non-gem' || normalizedType === 'nongem' || normalizedType === 'non gem') {
                    finalSheetType = 'Non-GeM'
                  }
                }
              }

              // Look for both "Value" and "ESTIMATED COST" columns
              let estimatedCostValue = 0

              // First try "Value" column (for GeM sheets)
              const valueCol = getCol(['Value', 'VALUE', 'BID VALUE', 'Contract Value'])
              if (valueCol) {
                const valueCell = row.getCell(valueCol)
                estimatedCostValue = parseCostValue(valueCell.value)
              }

              // If "Value" not found or is 0, try "ESTIMATED COST" (for Non-GeM sheets)
              if (estimatedCostValue === 0) {
                const estimatedCostCol = getCol(['ESTIMATED COST', 'COST', 'Estimated Cost'])
                if (estimatedCostCol) {
                  const costCell = row.getCell(estimatedCostCol)
                  estimatedCostValue = parseCostValue(costCell.value)
                }
              }

              // Skip tender if estimated cost/value is 0
              // if (estimatedCostValue === 0) {
              //   skippedRecords.value.zeroCost++
              //   return
              // }

              // Try to extract document fees if available
              let documentFees = 0
              const docFeesCol = getCol(['Document fees', 'FEES', 'TENDER FEE', 'Document Fees'])
              if (docFeesCol) {
                const feesCell = row.getCell(docFeesCol)
                documentFees = parseCostValue(feesCell.value)
              }

              // Try to extract EMD if available
              let emd = 0
              const emdCol = getCol(['EMD', 'EARNEST MONEY', 'Earnest Money Deposit'])
              if (emdCol) {
                const emdCell = row.getCell(emdCol)
                emd = parseCostValue(emdCell.value)
              }

              // Get additional fields with appropriate fallback values
              const msmeExemption = getFieldValue(msmeExemptionCol, 'NA')
              const startupExemption = getFieldValue(startupExemptionCol, 'NA')
              const quantity = getQuantityValue(quantityCol) // Use special function for quantity (defaults to "0")
              const eligibilityCriteria = getFieldValue(eligibilityCriteriaCol, 'NA')
              const checklist = getFieldValue(checklistCol, 'NA')

              // NEW: Get document required from seller (GeM specific)
              const documentRequiredFromSeller = getFieldValue(documentRequiredFromSellerCol, 'NA')

              // Create tender object with proper typing
              const tender: PartialTender = {
                srNo: String(rowNumber - 1),
                t247Id,
                referenceNo,
                tenderBrief: tenderBriefText,
                tenderBriefLink: tenderBriefLink || undefined,
                estimatedCost: estimatedCostValue,
                deadline: formattedDeadline,
                location: location || 'NA',
                organization: organization || 'NA',
                documentFees: documentFees,
                emd: emd,
                msmeExemption: msmeExemption,
                startupExemption: startupExemption,
                quantity: quantity,
                eligibilityCriteria: eligibilityCriteria,
                checklist: checklist,
                documentRequiredFromSeller: documentRequiredFromSeller,
                sheetType: finalSheetType,
                sourceFile: `${file.name} (${sheetName})`,
                isActive: true,
                status: 'new',
                selected: false,
                uploadedAt: new Date().toISOString(),
                workflowStatus: 'new',
                value: estimatedCostValue, // Assuming value is same as estimatedCost
                previousT247Id: null,
                rejectionComment: undefined,
                rejectedAt: undefined,
                assignmentNotes: undefined,
                assignedAt: undefined,
                wonAt: undefined,
                contractValue: undefined,
                contractStartDate: undefined,
                assignedToAgent: undefined,
                assignedAgentName: undefined,
                assignedAgentEmail: undefined,
                assignedAgentPhone: undefined,
                agentAssignedAt: undefined,
                agentNotes: undefined,
                agentSubmissionDate: undefined,
                agentSubmissionNotes: undefined
              }

              tenders.push(tender)
            } catch (rowError) {
              console.error(`Error processing row ${rowNumber}:`, rowError)
            }
          })
        })

        console.log(`✅ Extracted ${tenders.length} valid tenders`)
        console.log(`📊 Breakdown: ${tenders.filter(t => t.sheetType === 'GeM').length} GeM, ${tenders.filter(t => t.sheetType === 'Non-GeM').length} Non-GeM`)

        // Cast to Tender[] since we've filled in all required fields
        resolve(tenders as Tender[])

      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file)
  })
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''

  // Try multiple date formats
  const formats = [
    // DD-MM-YYYY
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/,
    // YYYY-MM-DD
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/,
  ]

  for (const format of formats) {
    const match = dateString.match(format)
    if (match) {
      let day, month, year

      if (match[1].length === 4) {
        // YYYY-MM-DD format
        year = match[1]
        month = match[2].padStart(2, '0')
        day = match[3].padStart(2, '0')
      } else {
        // DD-MM-YYYY format
        day = match[1].padStart(2, '0')
        month = match[2].padStart(2, '0')
        year = match[3]
      }

      const date = new Date(`${year}-${month}-${day}`)
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0]
      }
    }
  }

  // Try parsing as-is
  const date = new Date(dateString)
  if (!isNaN(date.getTime())) {
    return date.toISOString().split('T')[0]
  }

  return ''
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const saveToDatabase = async (tenders: Tender[]): Promise<void> => {
  savingToDatabase.value = true
  try {
    // Get token from auth store instead of localStorage directly
    const token = authStore.token // or authStore.user?.token depending on your store structure

    console.log('Token from store:', token ? 'Present' : 'Missing') // Debug log

    if (!token) {
      throw new Error('You must be logged in to upload tenders')
    }

    const response = await fetch(`${API_BASE_URL}/api/tenders/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ tenders })
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Your session has expired. Please log in again.')
      }

      const errorText = await response.text()
      console.error('Server error:', errorText)
      let errorData
      try {
        errorData = JSON.parse(errorText)
      } catch {
        errorData = { error: errorText }
      }
      throw new Error(errorData.error || `Server error: ${response.status}`)
    }

    const result = await response.json()
    console.log('✅ Save successful:', result.message)
    console.log('📊 Stats:', result.stats)

  } catch (error) {
    console.error('❌ Save error:', error)
    throw error
  }
}
</script>

<template>
  <div class="space-y-4">
    <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
      <input
        ref="fileInput"
        type="file"
        accept=".xlsx,.xls"
        @change="handleFileUpload"
        class="hidden"
      />

      <!-- Upload UI -->
      <div v-if="!uploading && !savingToDatabase" class="space-y-4">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"/>
        </svg>
        <div class="text-sm text-gray-600">
          <button
            @click="triggerFileInput"
            class="font-medium text-blue-600 hover:text-blue-500"
            type="button"
          >
            Click to upload
          </button>
          or drag and drop
        </div>
        <p class="text-xs text-gray-500">Excel files only (.xlsx, .xls)</p>
        <p class="text-xs text-gray-500 italic">
          Note: Records with blank Reference No or expired deadlines will be skipped
        </p>
        <p class="text-xs text-gray-500 italic">
          Note: Records with 0 estimated cost/value will be skipped
        </p>
      </div>

      <!-- Processing -->
      <LoaderDefault v-if="uploading" title="Processing Excel file..." />

      <div v-if="uploading" class="space-y-4">
        <div v-if="extractedLinksCount > 0" class="text-xs text-green-600">
          Found {{ extractedLinksCount }} hyperlinks
        </div>
        <div v-if="skippedRecords.blankRefNo > 0" class="text-xs text-yellow-600">
          Skipped {{ skippedRecords.blankRefNo }} blank Reference No
        </div>
        <div v-if="skippedRecords.expiredDeadline > 0" class="text-xs text-yellow-600">
          Skipped {{ skippedRecords.expiredDeadline }} expired deadlines
        </div>
        <div v-if="skippedRecords.watchlistSkipped > 0" class="text-xs text-purple-600">
          Preserved {{ skippedRecords.watchlistSkipped }} watchlist tenders (status unchanged)
        </div>
        <!-- <div v-if="skippedRecords.zeroCost > 0" class="text-xs text-yellow-600">
          Skipped {{ skippedRecords.zeroCost }} zero cost tenders
        </div> -->
      </div>

      <!-- Saving -->
      <LoaderDefault v-if="savingToDatabase" title="Saving to database..." />
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <!-- Success -->
    <div v-if="fileName && !uploading && !savingToDatabase" class="bg-green-50 rounded-lg p-4">
      <div class="flex items-center">
        <svg class="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <div>
          <p class="text-sm font-medium text-green-800">Upload Complete</p>
          <p class="text-sm text-green-600">{{ fileName }} ({{ fileSize }})</p>
          <p v-if="skippedRecords.watchlistSkipped > 0" class="text-xs text-purple-600 mt-1">
            ⚠️ {{ skippedRecords.watchlistSkipped }} watchlist tenders were preserved with their original status
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
